import type { ProjectFilters } from '../services/api';

/**
 * Serialise / deserialise the shared Project Essentials search state
 * (`q` + project dimension filters) into URL search params.
 *
 * The URL is the single source of truth for universal search: the search
 * bar on any page submits to `PE_SEARCH_PATH`, and the results page reads
 * everything back out of `location.search`. That keeps a search result
 * shareable, bookmarkable and survivable across a browser refresh.
 */

export interface PeSearchState {
  query: string;
  filters: ProjectFilters;
  /** Highlights page tab (`projects` | `insights`). */
  tab: string | null;
  /** Optional section anchor within a page (`periodic`, `reports`, …). */
  section: string | null;
  /** Item id to auto-open in FilePreviewDialog after navigation. */
  previewId: string | null;
}

/** Payload stashed before navigating so the destination can open a preview. */
export interface PePendingPreview {
  id: string;
  title: string;
  url: string;
  urlPublic?: string | null;
  filename?: string | null;
  forceBlobDownload?: boolean;
}

/** Optional `navigate(..., { state })` payload for auto-opening previews. */
export interface PeNavigateState {
  pePreviewAt?: number;
  pePendingPreview?: PePendingPreview | null;
}

const PENDING_PREVIEW_KEY = 'pe:pending-preview';

/** In-memory mirror so React Strict Mode double-effects can't lose the stash. */
let pendingPreviewMemory: PePendingPreview | null = null;

function normalizePreviewId(id: unknown): string {
  return id == null ? '' : String(id).trim();
}

/** Route that owns universal search results. */
export const PE_SEARCH_PATH = '/project-essential/search';

function splitCsv(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Write only filter params; leave `q` / `tab` / `section` alone. */
export function appendPeFilterParams(
  target: URLSearchParams,
  filters: ProjectFilters,
): URLSearchParams {
  if (filters.projectIds?.length) {
    target.set('projectId', filters.projectIds.join(','));
    target.delete('region');
    target.delete('district');
    target.delete('propertyType');
  } else {
    target.delete('projectId');
    if (filters.regions?.length) target.set('region', filters.regions.join(','));
    else target.delete('region');
    if (filters.districts?.length) target.set('district', filters.districts.join(','));
    else target.delete('district');
    if (filters.propertyTypes?.length) {
      target.set('propertyType', filters.propertyTypes.join(','));
    } else {
      target.delete('propertyType');
    }
  }
  if (filters.soleMarketing) target.set('soleMarketing', '1');
  else target.delete('soleMarketing');
  return target;
}

/** Build a `URLSearchParams` (or merge into `target`) from search state. */
export function appendPeSearchStateParams(
  target: URLSearchParams,
  state: Pick<PeSearchState, 'query' | 'filters'> & { previewId?: string | null },
): URLSearchParams {
  const trimmed = state.query.trim();
  if (trimmed) target.set('q', trimmed);
  else target.delete('q');
  appendPeFilterParams(target, state.filters);

  const previewId =
    state.previewId == null || state.previewId === ''
      ? ''
      : String(state.previewId).trim();
  if (previewId) target.set('preview', previewId);
  else target.delete('preview');

  return target;
}

/**
 * Replace filter params on the current query string, preserving `q`,
 * `tab`, and `section`. Returns `location.search` form (`?…` or ``).
 */
export function writePeFiltersToSearch(
  currentSearch: string,
  filters: ProjectFilters,
): string {
  const raw = currentSearch.startsWith('?') ? currentSearch.slice(1) : currentSearch;
  const params = new URLSearchParams(raw);
  appendPeFilterParams(params, filters);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Merge query + filters into a destination path that may already carry
 * `tab` / `section` (as returned by the search API's `viewMoreHref`).
 */
export function buildPeSearchHref(
  pathWithOptionalQuery: string,
  state: Pick<PeSearchState, 'query' | 'filters'> & { previewId?: string | null },
): string {
  const [path, existingQs = ''] = pathWithOptionalQuery.split('?', 2);
  const params = new URLSearchParams(existingQs);
  appendPeSearchStateParams(params, state);
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

/**
 * Catalogue nav href: carry active filters (including project ids)
 * from the current URL; drop `q`, `tab`, and `section`.
 */
export function buildPeCatalogueHref(destPath: string, currentSearch: string): string {
  const { filters } = parsePeSearchState(currentSearch);
  return buildPeSearchHref(destPath, { query: '', filters });
}

/** Link to the universal search results page for a query + filter set. */
export function buildPeSearchPageHref(
  query: string,
  filters: ProjectFilters = {},
): string {
  return buildPeSearchHref(PE_SEARCH_PATH, { query, filters });
}

/** Read search state from the current location's query string. */
export function parsePeSearchState(
  search: string | URLSearchParams,
): PeSearchState {
  const params =
    typeof search === 'string'
      ? new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
      : search;

  const projectIds = splitCsv(params.get('projectId'));
  const soleMarketing = params.get('soleMarketing') === '1';
  const filters: ProjectFilters = projectIds.length
    ? { projectIds }
    : {
        regions: splitCsv(params.get('region')),
        districts: splitCsv(params.get('district')),
        propertyTypes: splitCsv(params.get('propertyType')),
      };

  // Drop empty arrays so `{}` stays the idle filter identity.
  if (!filters.projectIds?.length) delete filters.projectIds;
  if (!filters.regions?.length) delete filters.regions;
  if (!filters.districts?.length) delete filters.districts;
  if (!filters.propertyTypes?.length) delete filters.propertyTypes;
  if (soleMarketing) filters.soleMarketing = true;
  else delete filters.soleMarketing;

  return {
    query: (params.get('q') || '').trim(),
    filters,
    tab: params.get('tab'),
    section: params.get('section'),
    previewId: (params.get('preview') || '').trim() || null,
  };
}

/** Stash a preview payload for the destination page to consume once. */
export function stashPePendingPreview(payload: PePendingPreview): void {
  const normalized: PePendingPreview = {
    ...payload,
    id: normalizePreviewId(payload.id),
  };
  pendingPreviewMemory = normalized;
  try {
    sessionStorage.setItem(PENDING_PREVIEW_KEY, JSON.stringify(normalized));
  } catch {
    // sessionStorage can throw in private mode / quota — memory still works.
  }
}

/**
 * Read a pending preview without clearing it. When `expectedId` is
 * supplied, only returns the payload if it matches (so a stale stash
 * can't open the wrong dialog). Cleared explicitly via
 * `clearPePendingPreview` once the dialog closes.
 */
export function peekPePendingPreview(
  expectedId?: string | null,
): PePendingPreview | null {
  const expected = normalizePreviewId(expectedId);
  const fromMemory = pendingPreviewMemory;
  if (fromMemory?.id && fromMemory.url && fromMemory.title) {
    if (!expected || normalizePreviewId(fromMemory.id) === expected) {
      return fromMemory;
    }
  }
  try {
    const raw = sessionStorage.getItem(PENDING_PREVIEW_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PePendingPreview;
    if (!parsed?.id || !parsed?.url || !parsed?.title) return null;
    parsed.id = normalizePreviewId(parsed.id);
    if (expected && parsed.id !== expected) return null;
    pendingPreviewMemory = parsed;
    return parsed;
  } catch {
    return null;
  }
}

export function clearPePendingPreview(): void {
  pendingPreviewMemory = null;
  try {
    sessionStorage.removeItem(PENDING_PREVIEW_KEY);
  } catch {
    // ignore
  }
}

export function countPeFilters(filters: ProjectFilters): number {
  return (
    (filters.regions?.length ?? 0) +
    (filters.districts?.length ?? 0) +
    (filters.propertyTypes?.length ?? 0) +
    (filters.projectIds?.length ?? 0) +
    (filters.soleMarketing ? 1 : 0)
  );
}

/** True when the user picked explicit project(s) in the filter dialog. */
export function hasPeProjectFilter(filters: ProjectFilters): boolean {
  return (filters.projectIds?.length ?? 0) > 0;
}

/**
 * Upcoming GLS listing / search heading. Executive Condo is not an
 * Enbloc pipeline, so drop the suffix whenever that bucket is on.
 */
export function peUpcomingGlsHeading(filters: ProjectFilters): string {
  return filters.propertyTypes?.includes('ec')
    ? 'Upcoming GLS'
    : 'Upcoming GLS / Enbloc';
}
